import type { HTMLAttributeAnchorTarget, ReactNode } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { Heading, Link, Text, VisuallyHidden } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './EmptyState.module.css';

export interface EmptyStateProps {
  heading: ReactNode;
  description: ReactNode;
  link?: {
    href: string;
    label: string;
    target?: HTMLAttributeAnchorTarget;
  };
}

function navigatesOutsideApp(href: string) {
  try {
    return new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Generic empty state component with heading, description and link
 */
export const EmptyState = ({ heading, description, link }: EmptyStateProps) => {
  const target = link?.target || '_blank';
  const isExternal = link !== undefined && navigatesOutsideApp(link.href);

  return (
    <div className={styles.container}>
      <Heading size="large" className={styles.emptyStateTitle}>
        {heading}
      </Heading>
      <Text className={styles.text}>{description}</Text>
      {link && (
        <Link href={link.href} className={styles.link} underline="always" target={target} rel="noopener noreferrer">
          {link.label}
          {isExternal && (
            <>
              <Icon name="link-external" size="small" />
              {target === '_blank' && (
                <VisuallyHidden elementType="span">
                  <Message id="ui.newTabLink.opensInNewTab" />
                </VisuallyHidden>
              )}
            </>
          )}
        </Link>
      )}
    </div>
  );
};
